import React, { useState } from 'react';
import axios from 'axios';
import { 
  MagnifyingGlassIcon,
  PhotoIcon,
  VideoCameraIcon,
  SpeakerWaveIcon,
  DocumentIcon,
  ClockIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://api.mythicalarc.com/api'
  : 'http://localhost:8000/api';

const MemorySearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(['image', 'video', 'audio', 'document']);
  const [includeFrames, setIncludeFrames] = useState(true);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        query: query.trim(),
        limit: '20',
        include_frames: includeFrames.toString()
      });
      
      selectedTypes.forEach(type => {
        params.append('media_types', type);
      });

      const response = await axios.get(`${API_BASE_URL}/media/search?${params}`);
      setResults(response.data.results);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const getMediaIcon = (type) => {
    switch (type) {
      case 'image': return <PhotoIcon className="w-5 h-5 text-blue-500" />;
      case 'video': return <VideoCameraIcon className="w-5 h-5 text-purple-500" />;
      case 'audio': return <SpeakerWaveIcon className="w-5 h-5 text-green-500" />;
      default: return <DocumentIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const toggleMediaType = (type) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Search Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Memory Search</h1>
        <p className="text-lg text-gray-600">
          Search across all your uploaded media using natural language
        </p>
      </div>

      {/* Search Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSearch} className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for anything... (e.g., 'photos of cats', 'video with music', 'documents about AI')"
              className="w-full pl-12 pr-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            />
          </div>

          {/* Filter Options */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Media Type Filters */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search in:
              </label>
              <div className="flex flex-wrap gap-2">
                {[
                  { type: 'image', label: 'Images', icon: PhotoIcon },
                  { type: 'video', label: 'Videos', icon: VideoCameraIcon },
                  { type: 'audio', label: 'Audio', icon: SpeakerWaveIcon },
                  { type: 'document', label: 'Documents', icon: DocumentIcon }
                ].map(({ type, label, icon: Icon }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleMediaType(type)}
                    className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedTypes.includes(type)
                        ? 'bg-blue-100 text-blue-800 border border-blue-300'
                        : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Advanced Options */}
            <div className="flex flex-col justify-end">
              <label className="flex items-center text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={includeFrames}
                  onChange={(e) => setIncludeFrames(e.target.checked)}
                  className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Include video frames
              </label>
            </div>
          </div>

          {/* Search Button */}
          <button
            type="submit"
            disabled={loading || !query.trim()}
            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed py-3 text-lg"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Searching...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <SparklesIcon className="w-5 h-5 mr-2" />
                Search Memory
              </div>
            )}
          </button>
        </form>
      </div>

      {/* Search Results */}
      {results.length > 0 && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Search Results ({results.length})
            </h2>
            <div className="text-sm text-gray-500">
              Query: "{query}"
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {results.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
                {/* Thumbnail */}
                <div className="aspect-video bg-gray-100 relative">
                  {item.thumbnail ? (
                    <img
                      src={item.thumbnail}
                      alt={item.caption || 'Search result'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {getMediaIcon(item.media_type)}
                    </div>
                  )}
                  
                  {/* Similarity Score */}
                  <div className="absolute top-2 right-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {Math.round(item.similarity_score * 100)}% match
                    </span>
                  </div>

                  {/* Video Frame Indicator */}
                  {item.is_video_frame && (
                    <div className="absolute top-2 left-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Frame {item.frame_number}
                      </span>
                    </div>
                  )}

                  {/* Duration */}
                  {item.duration && (
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                      {formatDuration(item.duration)}
                    </div>
                  )}

                  {/* Frame Timestamp */}
                  {item.is_video_frame && item.timestamp_seconds !== undefined && (
                    <div className="absolute bottom-2 left-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                      {formatDuration(item.timestamp_seconds)}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  {/* Media Type and Icon */}
                  <div className="flex items-center mb-2">
                    {getMediaIcon(item.media_type)}
                    <span className="ml-2 text-sm font-medium text-gray-900 capitalize">
                      {item.media_type}
                      {item.is_video_frame && ' Frame'}
                    </span>
                  </div>

                  {/* Content/Caption */}
                  {item.caption && (
                    <p className="text-sm text-gray-800 mb-2 line-clamp-2">
                      {item.caption}
                    </p>
                  )}

                  {/* Extracted Text */}
                  {item.extracted_text && item.extracted_text.length > 0 && (
                    <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                      📝 {item.extracted_text}
                    </p>
                  )}

                  {/* Match Reason */}
                  <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                    {item.content}
                  </p>

                  {/* Metadata */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center">
                      <ClockIcon className="w-3 h-3 mr-1" />
                      {formatDate(item.timestamp)}
                    </div>
                    <div className="font-medium">
                      {Math.round(item.similarity_score * 100)}%
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Results */}
      {query && !loading && results.length === 0 && (
        <div className="text-center py-12">
          <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
          <p className="text-gray-500">
            Try different keywords or check your media type filters
          </p>
        </div>
      )}

      {/* Search Tips */}
      {!query && (
        <div className="bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-4">
            💡 Search Tips
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
            <div>
              <h4 className="font-medium mb-2">Try searching for:</h4>
              <ul className="space-y-1">
                <li>• "photos of people"</li>
                <li>• "videos with music"</li>
                <li>• "documents about technology"</li>
                <li>• "images with text"</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Advanced features:</h4>
              <ul className="space-y-1">
                <li>• Searches captions and extracted text</li>
                <li>• Includes audio transcriptions</li>
                <li>• Finds specific video frames</li>
                <li>• Works with natural language</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemorySearch;
